import React, { useEffect, useRef, useState } from "react";

import ErrorIcon from "@uui/AlertCircle";
import SuccessIcon from "@uui/CheckCircle";
import PlusIcon from "@uui/PlusCircle";

import type { SxProps } from "@mui/material";
import Typography from "@mui/material/Typography";

import AddReadInput from "@app/analytics/components/AddReadInput";
import SideNavButtonIcon from "@app/analytics/components/SideNav/SideNavButtonIcon";
import useAddRead from "@app/analytics/hooks/meetings/useAddRead";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";

import Link from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import { Row } from "@rui/Stack";

import { SideNavButton, SideNavItemContainer } from "./styles";

const INPUT_STYLE_OVERRIDES: SxProps = {
  width: "100%",
  minWidth: "100%",
  "& .MuiInputBase-root": {
    height: "36px",
    backgroundColor: "transparent.white.12",
  },
  input: {
    color: "#FFF",
    fontSize: "0.875rem",
    "&::placeholder": { color: "transparent.white.50" },
  },
  "& .MuiInputBase-adornedEnd": {
    paddingRight: 0,
  },
  "& .MuiInputAdornment-positionEnd .MuiButton-root": {
    height: "36px",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "shades.primary.500",
  },
};

const SIDE_NAV_BUTTON_STYLE_OVERRIDES: SxProps = {
  backgroundColor: "transparent.white.4",
  "&:hover": {
    backgroundColor: "shades.primary.500",
  },
};

const handleValidationFail = () =>
  toast.error("Please input a valid https URL");

const SideNavAddReadInput = ({
  onDone,
}: {
  onDone: (timeout?: number) => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { addRead, hasInputError, meetingUrl, onInputChange, reset, validate } =
    useAddRead({
      onValidationFail: handleValidationFail,
      uiLocation: "side_nav",
    });
  const handleAddRead = () => {
    const isValid = validate(meetingUrl);
    if (isValid) {
      addRead.mutate(
        { meetingUrl },
        {
          onSuccess: () => {
            onDone(2000);
            reset();
          },
        }
      );
    }
  };

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleBlur = () => {
    if (!meetingUrl) onDone();
  };

  const isError = addRead.isError || hasInputError;

  return (
    <>
      {addRead.isSuccess ? (
        <SideNavItemContainer expanded={false}>
          <Row variant="left" spacing={1} padding={1}>
            <SuccessIcon
              sx={{ fontSize: "16px", color: "shades.meadow.600" }}
            />
            <Typography variant="bodySecondaryReg" noWrap>
              Read has been added!
            </Typography>
          </Row>
        </SideNavItemContainer>
      ) : addRead.isError ? (
        <SideNavItemContainer expanded={false}>
          <Row variant="left" spacing={1} padding={1}>
            <ErrorIcon sx={{ fontSize: "16px", color: "error.main" }} />
            <Typography variant="labelReg">
              There was an error adding Read.{" "}
              <Link
                color="shades.secondary.300"
                underline
                onClick={addRead.reset}
              >
                Retry
              </Link>{" "}
              or{" "}
              <Link
                color="shades.secondary.300"
                underline
                href={LINKS.SUPPORT.MANUALLY_ADD_READ}
              >
                troubleshoot
              </Link>
              .
            </Typography>
          </Row>
        </SideNavItemContainer>
      ) : addRead.isPending ? (
        <SideNavItemContainer direction="row" expanded={false}>
          <Row variant="left" spacing={1} padding={1}>
            <LoadSpinner size={16} />
            <Typography variant="bodySecondaryReg" noWrap>
              Adding Read...
            </Typography>
          </Row>
        </SideNavItemContainer>
      ) : (
        <AddReadInput
          ref={inputRef}
          error={isError}
          noStartAdornment
          placeholder="Paste meeting URL"
          onBlur={handleBlur}
          onChange={onInputChange}
          onSubmit={handleAddRead}
          sx={INPUT_STYLE_OVERRIDES}
          value={meetingUrl}
        />
      )}
    </>
  );
};

const SideNavAddRead = ({ expanded }: { expanded: boolean }) => {
  const [showInput, setShowInput] = useState(false);

  const clearInput = (timeout = 0) =>
    setTimeout(() => setShowInput(false), timeout);

  useEffect(() => {
    if (!expanded && showInput) setShowInput(false);
  }, [expanded]);

  if (!expanded || !showInput) {
    return (
      <SideNavButton
        expanded={expanded}
        onClick={() => setShowInput(true)}
        sx={SIDE_NAV_BUTTON_STYLE_OVERRIDES}
      >
        {expanded ? (
          <Row variant="left" spacing={1}>
            <SideNavButtonIcon Icon={PlusIcon} />
            <Typography variant="bodySecondaryReg" noWrap>
              Add to live meeting
            </Typography>
          </Row>
        ) : (
          <SideNavButtonIcon Icon={PlusIcon} />
        )}
      </SideNavButton>
    );
  }

  return <SideNavAddReadInput onDone={clearInput} />;
};

export default SideNavAddRead;
